import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { translations, languages, DEFAULT_LANG } from "./translations";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key) => key,
  languages,
});

// Non-sensitive UI preference. localStorage is intentional here: language
// choice is a display-only setting, not a credential or PII, and must survive
// a browser reload without a server round-trip.
const STORAGE_KEY = "bw_lang";

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_LANG;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && translations[stored]) return stored;
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    return translations[nav] ? nav : DEFAULT_LANG;
  });

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!translations[next]) return;
    localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
  }, []);

  const t = useCallback(
    (key) => {
      const dict = translations[lang] || translations[DEFAULT_LANG];
      const fallback = translations[DEFAULT_LANG];
      return key in dict ? dict[key] : fallback[key] || key;
    },
    [lang]
  );

  const value = { lang, setLang, t, languages };

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
